import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Card, CardContent } from '@mui/material';
import { volunteerService } from '../services/api';
import { ASSETS } from '../utils/assets';
import SimplePageBanner from '../components/SimplePageBanner';
import SiteStatsItems from '../components/SiteStatsItems';
import { bodyTextSx, primaryButtonSx, cardSx, BRAND, sectionPaddingSx } from '../theme/pageStyles';

const VolunteerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    skills: '',
    availability: '',
    motivation_message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await volunteerService.submitApplication(formData);
      setSubmitted(true);
      setFormData({
        name: '', email: '', phone: '', address: '', city: '', state: '',
        skills: '', availability: '', motivation_message: ''
      });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert('Error submitting application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <SimplePageBanner
        subtitle="Join our mission"
        title="Volunteer With Us"
        description="Share your time and skills to help us create meaningful change in the communities we serve."
      />

      <Box sx={{ backgroundColor: '#FAF9F6', ...sectionPaddingSx }}>
        <Container maxWidth="md" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
        <Card sx={cardSx} elevation={0}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {submitted ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ ...bodyTextSx, fontWeight: 600, color: BRAND.green, mb: 2 }}>
                  ✓ Application Submitted Successfully!
                </Typography>
                <Typography sx={bodyTextSx}>
                  Thank you for your interest in joining Roots & Wings. We'll review your application and contact you within 3-5 business days.
                </Typography>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Typography sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: 20, color: '#2D2929', mb: 4 }}>
                  Volunteer Application Form
                </Typography>

                <TextField
                  fullWidth
                  label="Full Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Email Address *"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Phone Number *"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                  required
                />

                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={2}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2, mb: 2 }}>
                  <TextField
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                  <TextField
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Skills & Expertise"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={3}
                  placeholder="E.g., Teaching, Healthcare, Writing, etc."
                />

                <TextField
                  fullWidth
                  label="Availability"
                  name="availability"
                  value={formData.availability}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={2}
                  placeholder="E.g., Weekends, 5 hours/week, etc."
                />

                <TextField
                  fullWidth
                  label="Why do you want to volunteer? *"
                  name="motivation_message"
                  value={formData.motivation_message}
                  onChange={handleChange}
                  margin="normal"
                  multiline
                  rows={4}
                  required
                />

                <Button 
                  variant="contained" 
                  fullWidth 
                  sx={{ ...primaryButtonSx, mt: 4 }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        </Container>
      </Box>

      {/* Join With Us - stats bar (same as Home/Contact) */}
      <Box sx={{ backgroundColor: '#fff', py: { xs: 6, md: 8 } }}>
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Typography sx={{ fontFamily: '"Caveat", cursive', fontWeight: 700, fontSize: { xs: 24, md: 27 }, color: '#313530', textAlign: 'center', mb: '25px' }}>Join With Us</Typography>
          <Typography component="h2" sx={{ fontFamily: '"Nunito", sans-serif', fontWeight: 900, fontSize: { xs: 26, sm: 34, md: 40 }, lineHeight: 1.25, letterSpacing: '0.01em', color: '#2D2929', textAlign: 'center', mb: { xs: '30px', md: '50px' } }}>
            Join in Roots & Wings
            <br />
            for Better Tomorrow
          </Typography>
          <Box sx={{ position: 'relative', overflow: 'hidden', maxWidth: 1200, mx: 'auto', borderRadius: { xs: 16, md: '23px' }, minHeight: { xs: 200, md: 238 }, display: 'flex', flexDirection: 'column', border: '1px solid #c4a574', backgroundImage: `url(${ASSETS.parallax})`, backgroundSize: 'cover', backgroundPosition: 'center', '&::before': { content: '""', position: 'absolute', inset: 0, backgroundColor: 'rgba(36, 88, 78, 0.63)', borderRadius: '23px' } }}>
            <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', justifyContent: 'space-evenly', alignItems: 'center', flex: 1, minHeight: { xs: 200, md: 238 }, gap: { xs: 3, sm: 0 }, py: { xs: 4, md: 5 }, px: { xs: 3, md: 4 } }}>
              <SiteStatsItems />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VolunteerPage;
